import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { MdOutlineArrowBack } from "react-icons/md";
import { Table } from "react-bootstrap";
import PageContainer from "../../../components/PageContainer";
import RelatorioApiService from "../../../services/api/RelatorioApiService";
import Loading from "../../../components/Loading";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine
} from "recharts";

/** Utils */
const formatDateToYYYYMM = (date) => {
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
};

const getMonthName = (anoMes) => {
    const [year, month] = anoMes.split("-");
    const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
    return date.toLocaleDateString("pt-BR", { month: "long" }).replace(/^./, (c) => c.toUpperCase());
};

const formatPercent = (value) => {
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    return `${num.toFixed(2)}%`;
};

const MESES = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

/** Tooltip Customizado para o grafico */
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div
                className="custom-tooltip"
                role="tooltip"
                style={{
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    padding: "10px",
                    borderRadius: "5px"
                }}
            >
                <p><strong>Mês:</strong> {label}</p>
                <p><strong>Turnover:</strong> {payload[0].value.toFixed(2)}%</p>
                <p><strong>Média Acumulada:</strong> {payload[1]?.value?.toFixed(2)}%</p>
            </div>
        );
    }
    return null;
};

function RelatorioTurnoverView() {
    const history = useHistory();
    const location = useLocation();
    const { periodoInicial, periodoFinal } = location.state?.data || {};

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [relatorioData, setRelatorioData] = useState({});
    const [metaTurnover, setMetaturnover] = useState(0)
    const [anoSelecionado, setAnoSelecionado] = useState("");

    const fetchRelatorioData = useCallback(async () => {
        if (!periodoInicial || !periodoFinal) {
            setError("Períodos inicial e final são obrigatórios");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const dataInicialFormatada = formatDateToYYYYMM(periodoInicial);
            const dataFinalFormatada = formatDateToYYYYMM(periodoFinal);

            const response = await RelatorioApiService.getRelatorioTurnover(
                dataInicialFormatada,
                dataFinalFormatada
            );

            const { result } = response.data;

            setRelatorioData(result);
            
            const anos = Object.keys(result);
            if (anos.length > 0) {
                setAnoSelecionado((prev) => prev || anos[0]); // evita resetar se usuário já selecionou manualmente
            }
        } catch (err) {
            console.error("Erro ao buscar dados do relatório:", err);
            setError("Erro ao carregar dados do relatório. Tente novamente.");
        } finally {
            setLoading(false);
        }
    }, [periodoInicial, periodoFinal]);

    const fetchMetaTurnover = useCallback(async () => {
        try {
            const meta = await RelatorioApiService.getMetaTurnover();

            setMetaturnover(meta.data.meta);
        } catch (err) {
            console.error("Erro ao buscar meta tornover:", err);
            setError("Erro ao carregar dados do relatório, Tente novamente.")
        }
    }, [])

    useEffect(() => {
        fetchRelatorioData();
        fetchMetaTurnover();
    }, [fetchRelatorioData, fetchMetaTurnover]);

    // Obtém todos os anos disponíveis
    const anosDisponiveis = useMemo(() => {
        return Object.keys(relatorioData).sort();
    }, [relatorioData]);

    const dadosAnoSelecionado = useMemo(() => {
        if (!relatorioData[anoSelecionado]) return [];

        return Object.entries(relatorioData[anoSelecionado])
            .map(([mes, data]) => ({
                ...data,
                mesLabel: mes
            }))
            .sort((a, b) => MESES.indexOf(a.mesLabel) - MESES.indexOf(b.mesLabel));
    }, [relatorioData, anoSelecionado]);

    // Dados para o gráfico do ano selecionado
    const dataGrafico = useMemo(() => {
        return dadosAnoSelecionado.map(({ mesLabel, turnover, mediaAcumulada }) => ({
            month: mesLabel,
            turnover: parseFloat(turnover) || 0,
            mediaAcumulada: parseFloat(mediaAcumulada) || 0
        }));
    }, [dadosAnoSelecionado]);

    const metaAnoSelecionado = useMemo(() => {
        const meses = Object.keys(relatorioData[anoSelecionado] || {});
        if (!meses.length) return 0;
        return relatorioData[anoSelecionado][meses[0]]?.meta || 0;
    }, [anoSelecionado, relatorioData]);


    const actions = useMemo(
        () => [
            {
                label: "Voltar",
                icon: <MdOutlineArrowBack />,
                color: "info",
                type: "button",
                onClick: () => history.goBack()
            }
        ],
        [history]
    );

    const title = `Relatório de Turnover - ${getMonthName(formatDateToYYYYMM(periodoInicial))} a ${getMonthName(formatDateToYYYYMM(periodoFinal))}`;

    if (error) {
        return (
            <PageContainer title="Relatório de Turnover" actions={actions}>
                <div className="alert alert-danger">{error}</div>
            </PageContainer>
        );
    }

    return (
        <PageContainer title={title} actions={actions}>
            {loading ? (
                <Loading message="Carregando dados..." />
            ) : (
                <>
                    {Object.keys(relatorioData).length === 0 && (
                        <div className="alert alert-warning">
                            Nenhum dado disponível para o período selecionado.
                        </div>
                    )}

                    {Object.keys(relatorioData).length > 0 && (
                        <>
                            {/* Tabela de meta */}
                            <Table striped bordered hover className="mb-3 w-25">
                                <thead>
                                    <tr>
                                        <th>Meta Atual</th>
                                        <th style={{ color: "#ff7300" }}>
                                            {formatPercent(metaTurnover)}
                                        </th>
                                    </tr>
                                </thead>
                            </Table>

                            {/* Listagem completa de todos os anos e meses */}
                            {anosDisponiveis.map((ano) => {
                                const meses = Object.keys(relatorioData[ano]);
                                const dadosAno = meses.map(mes => ({
                                    ...relatorioData[ano][mes],
                                    mesLabel: mes
                                })).sort((a, b) => {
                                    return MESES.indexOf(a.mesLabel) - MESES.indexOf(b.mesLabel);
                                });

                                return (
                                    <React.Fragment key={ano}>
                                        <h5><b>Ano:</b> {ano}</h5>
                                        <Table striped bordered hover className="mb-5">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Mês</th>
                                                    <th>Desligamentos</th>
                                                    <th>Admissões</th>
                                                    <th>Colaboradores</th>
                                                    <th>Turnover</th>
                                                    <th>Média Acumulada</th>
                                                    <th>Meta</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {dadosAno.map((item, index) => (
                                                    <tr key={`${item.anoMes}-${item.colaboradores}-${item.desligamentos}`}>
                                                        <td>{index + 1}</td>
                                                        <td>{item.mesLabel}</td>
                                                        <td>{item.desligamentos}</td>
                                                        <td>{item.admissoes}</td>
                                                        <td>{item.colaboradores}</td>
                                                        <td>{formatPercent(item.turnover)}</td>
                                                        <td>{formatPercent(item.mediaAcumulada)}</td>
                                                        <td>{formatPercent(item.meta)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </React.Fragment>
                                );
                            })}

                            {/* Filtro de ano para o gráfico */}
                            <div className="w-100 d-flex justify-content-center mb-3">
                                <select
                                    style={{
                                        padding: "8px 16px",
                                        borderRadius: "10px",
                                        border: "1px solid #555",
                                        color: "#555",
                                        background: "transparent"
                                    }}
                                    value={anoSelecionado}
                                    onChange={(e) => setAnoSelecionado(e.target.value)}
                                >
                                    {anosDisponiveis.map((ano) => (
                                        <option key={ano} value={ano}>{ano}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Gráfico do ano selecionado */}
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart data={dataGrafico} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis tickFormatter={(value) => `${value.toFixed(2)}%`} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Bar dataKey="turnover" fill="#6265ea" name="Turnover" />
                                    <Bar dataKey="mediaAcumulada" fill="#00c49f" name="Média Acumulada" />
                                    <ReferenceLine
                                        y={metaAnoSelecionado}
                                        stroke="#ff7300"
                                        strokeDasharray="4 4"
                                        strokeWidth={2}
                                        label={`Meta ${formatPercent(metaAnoSelecionado)}`}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </>
                    )}
                </>
            )}
        </PageContainer>
    );
}

export default RelatorioTurnoverView;
